import { memo } from 'react';
import { motion } from 'framer-motion';
import {
  Heart,
  MessageCircle,
  Bookmark,
  ShoppingBag,
  Share2,
  MoreHorizontal,
} from 'lucide-react';
import type { FeedItem } from '@/types';

interface FeedCardProps {
  item: FeedItem;
  onLike: (item: FeedItem) => void;
  onSave: (item: FeedItem) => void;
  onShop: (item: FeedItem) => void;
  onProfile: (userId: string) => void;
  onComment?: (item: FeedItem) => void;
  onShare?: (item: FeedItem) => void;
  onMore?: (item: FeedItem) => void;
}

export const FeedCard = memo(function FeedCard({
  item,
  onLike,
  onSave,
  onShop,
  onProfile,
  onComment,
  onShare,
  onMore,
}: FeedCardProps) {
  const timeAgo = getTimeAgo(item.createdAt);

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-card overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -1 }}
    >
      {/* User header */}
      <div className="flex items-center gap-3 p-3">
        <button
          onClick={() => onProfile(item.user.id)}
          className="flex items-center gap-2.5 flex-1 min-w-0"
        >
          <img
            src={item.user.avatarUrl ?? '/placeholder-avatar.png'}
            alt={item.user.displayName}
            className="w-9 h-9 rounded-full object-cover ring-2 ring-vestir-purple/20"
          />
          <div className="text-left min-w-0">
            <p className="text-sm font-semibold truncate">{item.user.displayName}</p>
            <p className="text-[10px] text-vestir-gray-dark">{timeAgo}</p>
          </div>
        </button>
        <button
          onClick={() => onMore?.(item)}
          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-vestir-gray/30"
          aria-label="More options"
        >
          <MoreHorizontal size={16} className="text-vestir-gray-dark" />
        </button>
      </div>

      {/* Image */}
      <div className="relative aspect-[4/5] bg-vestir-gray/20">
        <img
          src={item.imageUrl}
          alt={item.caption}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Shop button overlay */}
        {item.purchaseLinks.length > 0 && (
          <button
            onClick={() => onShop(item)}
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-2
                       bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium
                       shadow-lg hover:bg-white transition-colors"
          >
            <ShoppingBag size={14} className="text-vestir-purple" />
            Shop This Look
          </button>
        )}

        {/* Type badge */}
        {item.type !== 'outfit' && (
          <span className="absolute top-3 left-3 px-2 py-1 bg-vestir-coral/90 text-white
                          text-[10px] font-medium rounded-full capitalize">
            {item.type}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onLike(item)}
              className="flex items-center gap-1.5 group"
              aria-label={item.isLiked ? 'Unlike' : 'Like'}
            >
              <motion.div
                whileTap={{ scale: 1.3 }}
                transition={{ type: 'spring', stiffness: 400, damping: 10 }}
              >
                <Heart
                  size={20}
                  className={`transition-colors ${
                    item.isLiked
                      ? 'fill-vestir-coral text-vestir-coral'
                      : 'text-vestir-black group-hover:text-vestir-coral'
                  }`}
                />
              </motion.div>
              <span className="text-xs font-medium">{formatCount(item.likes)}</span>
            </button>

            <button
              onClick={() => onComment?.(item)}
              className="flex items-center gap-1.5"
              aria-label="Comments"
            >
              <MessageCircle size={18} className="text-vestir-black" />
              <span className="text-xs font-medium">{formatCount(item.commentsCount)}</span>
            </button>

            <button onClick={() => onShare?.(item)} aria-label="Share">
              <Share2 size={18} className="text-vestir-black" />
            </button>
          </div>

          <button
            onClick={() => onSave(item)}
            aria-label={item.isSaved ? 'Unsave' : 'Save'}
          >
            <Bookmark
              size={20}
              className={`transition-colors ${
                item.isSaved
                  ? 'fill-vestir-purple text-vestir-purple'
                  : 'text-vestir-black'
              }`}
            />
          </button>
        </div>

        {/* Caption */}
        {item.caption && (
          <p className="text-sm">
            <span className="font-semibold">{item.user.displayName}</span>{' '}
            {item.caption}
          </p>
        )}

        {/* Tags */}
        {item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-vestir-purple font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Comments preview */}
        {item.commentsCount > 0 && (
          <button onClick={() => onComment?.(item)} className="text-xs text-vestir-gray-dark mt-1.5 block">
            View all {item.commentsCount} comments
          </button>
        )}
      </div>
    </motion.div>
  );
});

function getTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

function formatCount(count: number): string {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
}
